/*
 * Copyright (c) 2026, Gather
 * https://gather.life
 */

import { type ReactNode } from 'react';
import Box from '@mui/material/Box/Box';
import Collapse from '@mui/material/Collapse/Collapse';
import Divider from '@mui/material/Divider/Divider';
import Paper from '@mui/material/Paper/Paper';
import Stack from '@mui/material/Stack/Stack';
import { styled } from '@mui/material/styles';
import { type TransitionProps } from '@mui/material/transitions';

import {
  AppSpacing,
  BorderOptions,
  BorderRadius,
  GatherShadows,
} from '@marketplace/gather-ui/theme';

import { QuickBodyTypes } from './quick-search/body-type.component';
import { QuickPopularMakes } from './quick-search/popular-makes.component';
import { useSearchDropdown } from './search-dropdown.provider';
import { type UpdateSearchValueParams } from '../search.component';
import { Suggestions } from './suggestions/suggestions.component';
import { SearchHistory } from './search-history/search-history.component';

// shadow wrapper sits outside collapse - covers both input area and dropdown for smooth shadow
const DropdownShadowWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: `${BorderRadius.XXL}px`,
  boxShadow: isActive ? GatherShadows.elevation3 : 'none',
  // Mobile: no shadow, use border instead
  [theme.breakpoints.down('md')]: {
    boxShadow: 'none',
    borderRadius: `${BorderRadius.Alert}px`,
  },
}));

const DropdownSurface = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: `0 0 ${BorderRadius.XXL}px ${BorderRadius.XXL}px`,
  // Mobile: add border to match input wrapper
  [theme.breakpoints.down('md')]: {
    borderRadius: `0 0 ${BorderRadius.Alert}px ${BorderRadius.Alert}px`,
    borderLeft: BorderOptions.BorderInputActive,
    borderRight: BorderOptions.BorderInputActive,
    borderBottom: BorderOptions.BorderInputActive,
  },
}));

const DropdownContent = styled(Box)(() => ({
  padding: AppSpacing.MD,
}));

const styleMx = { mx: AppSpacing.MD };
const borderRadiusSx = { borderRadius: 2 };

type SearchDropdownProps = {
  /**
   * Transition data passed from the popper to the collapse component so that
   * the animations work correctly.
   */
  transitionProps?: TransitionProps;
  updateSearchValue: (options: UpdateSearchValueParams) => Promise<void>;
  value: string;
  yOffset: number | null;
};

export function SearchDropdown({
  transitionProps,
  updateSearchValue,
  value,
  yOffset,
}: SearchDropdownProps): ReactNode {
  const { dropdownState, animationIsDone } = useSearchDropdown();

  if (value.length > 0) {
    return (
      <DropdownShadowWrapper isActive={dropdownState !== 'closed'} sx={borderRadiusSx}>
        <Box height={yOffset ?? 0} width={1} />
        <Collapse {...transitionProps} timeout={300} onExited={animationIsDone}>
          <DropdownSurface elevation={0}>
            {value.length >= 3 ? (
              <>
                <Divider sx={styleMx} />
                <DropdownContent>
                  <Suggestions updateSearchValue={updateSearchValue} value={value} />
                </DropdownContent>
              </>
            ) : null}
          </DropdownSurface>
        </Collapse>
      </DropdownShadowWrapper>
    );
  }

  return (
    <DropdownShadowWrapper isActive={dropdownState !== 'closed'} sx={borderRadiusSx}>
      <Box height={yOffset ?? 0} width={1} />
      <Collapse {...transitionProps} timeout={300} onExited={animationIsDone}>
        <DropdownSurface elevation={0}>
          <Divider sx={styleMx} />
          <DropdownContent>
            <Stack spacing={4}>
              <SearchHistory updateSearchValue={updateSearchValue} />
              <QuickBodyTypes updateSearchValue={updateSearchValue} />
              <QuickPopularMakes updateSearchValue={updateSearchValue} />
            </Stack>
          </DropdownContent>
        </DropdownSurface>
      </Collapse>
    </DropdownShadowWrapper>
  );
}
